// 위젯 C — 종결처리 (12단계 종결)
// 원천: 남양주시 「N-CARE 통합돌봄 플랫폼 구축 계획(안)」 12단계
//
// 시각 원칙: 이달 종결 총건 + 사유별 분포(스택 바) + 평균 서비스 기간 + 상정 대기.
// 종결이 잘 처리되고 있는지, 어떤 사유가 지배적인지 한 눈에.
import * as React from "react";
import { MetricDrilldownModal } from "../../MetricDrilldownModal";

interface Reason {
  code: string;
  label: string;
  count: number;
}

interface Props {
  data: {
    region: { name: string; code: string };
    generated_at?: string;
    totals: {
      month_closed: number;
      week_closed: number;
      avg_service_days: number | null;
      active_caring: number;
      pending_closure: number;
    };
    reasons: Reason[];
  };
}

// 사유별 색상 — 긍정/중립/부정 톤 구분.
const REASON_COLOR: Record<string, string> = {
  recovery:        "var(--green-500)",   // 목표달성 = 긍정
  transferred:     "var(--sky-500)",     // 이관 = 중립
  moved:           "var(--sky-500)",     // 전출 = 중립
  hospitalization: "var(--amber-500)",   // 입원 = 주의
  declined:        "var(--amber-500)",   // 거부 = 주의
  deceased:        "var(--ink-3)",       // 사망 = 무채색
  other:           "var(--ink-4)",       // 기타
};

export function ClosureBoard({ data }: Props) {
  const { totals, reasons } = data;
  // 숫자 클릭 → 그 숫자를 이루는 케이스 목록(카드와 같은 스코프).
  const [drill, setDrill] = React.useState<{ metric: string; reason?: string } | null>(null);
  const monthTotal = totals.month_closed;
  const monthMonths = totals.avg_service_days ? (totals.avg_service_days / 30).toFixed(1) : null;

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div style={{ padding: "18px 22px 12px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div className="h3">종결 처리 · 이달 요약</div>
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            서비스 성과 · 종결 사유 분포 · 평균 지원 기간
          </div>
        </div>
        {totals.pending_closure > 0 && (
          <span role="button" tabIndex={0} className="badge badge-amber"
            title="케어 중이면서 종결 사유가 기록된 케이스(상정 대기) — 클릭 시 목록"
            onClick={() => setDrill({ metric: "pending_closure" })}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDrill({ metric: "pending_closure" }); } }}
            style={{ cursor: "pointer" }}>
            상정 대기 <b style={{ marginLeft: 4 }}>{totals.pending_closure}</b>
          </span>
        )}
      </div>

      {/* 헤드라인 숫자 3종 */}
      <div style={{ padding: "6px 22px 14px", display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 10 }}>
        <StatBox
          label="이달 종결"
          onOpen={monthTotal > 0 ? () => setDrill({ metric: "month_closed" }) : null}
          title={monthTotal > 0 ? "이달 종결된 케이스 목록 보기" : "이달 종결된 케이스가 없습니다"}
          style={{ background: "var(--green-50)", borderLeft: "3px solid var(--green-500)" }}
        >
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--green-600)", fontVariantNumeric: "tabular-nums", marginTop: 2, lineHeight: 1 }}>
            {monthTotal.toLocaleString("ko-KR")}
          </div>
          <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>
            이번주{" "}
            <b
              role={totals.week_closed > 0 ? "button" : undefined}
              tabIndex={totals.week_closed > 0 ? 0 : -1}
              title={totals.week_closed > 0 ? "이번주 종결 케이스만 보기" : undefined}
              onClick={(e) => { if (totals.week_closed > 0) { e.stopPropagation(); setDrill({ metric: "week_closed" }); } }}
              onKeyDown={(e) => { if (totals.week_closed > 0 && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); e.stopPropagation(); setDrill({ metric: "week_closed" }); } }}
              style={{ color: "var(--ink-2)", fontWeight: 700, cursor: totals.week_closed > 0 ? "pointer" : "default",
                       textDecoration: totals.week_closed > 0 ? "underline" : "none", textDecorationStyle: "dotted", textUnderlineOffset: 3 }}
            >{totals.week_closed}</b>
          </div>
        </StatBox>
        <StatBox
          label="평균 서비스 기간"
          onOpen={monthTotal > 0 ? () => setDrill({ metric: "service_days" }) : null}
          title={monthTotal > 0 ? "이달 종결 케이스를 지원 기간이 긴 순으로 보기" : "종결 데이터가 없습니다"}
        >
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--ink-1)", fontVariantNumeric: "tabular-nums", marginTop: 2, lineHeight: 1 }}>
            {monthMonths ? <>{monthMonths}<span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginLeft: 3 }}>개월</span></> : "—"}
          </div>
          <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>
            {totals.avg_service_days ? `${totals.avg_service_days.toLocaleString("ko-KR")}일` : "종결 데이터 부족"}
          </div>
        </StatBox>
        <StatBox
          label="진행 중"
          onOpen={totals.active_caring > 0 ? () => setDrill({ metric: "active_caring" }) : null}
          title={totals.active_caring > 0 ? "케어 중 케이스 목록 보기" : "케어 중 케이스가 없습니다"}
        >
          <div style={{ fontSize: 22, fontWeight: 800, color: "var(--sky-600)", fontVariantNumeric: "tabular-nums", marginTop: 2, lineHeight: 1 }}>
            {totals.active_caring.toLocaleString("ko-KR")}
          </div>
          <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>케어 중 케이스</div>
        </StatBox>
      </div>

      {/* 사유 스택 바 */}
      <div style={{ padding: "0 22px 22px" }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: "var(--ink-2)", letterSpacing: 0.5, marginBottom: 8 }}>이달 종결 사유</div>
        {monthTotal === 0 ? (
          <div className="muted" style={{ fontSize: 11, padding: "16px 0", textAlign: "center" }}>
            이달 종결된 케이스가 없습니다.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", height: 14, borderRadius: 99, overflow: "hidden", background: "var(--bg-sub)" }}>
              {reasons.filter(r => r.count > 0).map((r) => {
                const width = (r.count / monthTotal) * 100;
                return (
                  <div key={r.code} role="button" tabIndex={0}
                    title={`${r.label} ${r.count}건 (${Math.round(width)}%) · 클릭 시 해당 건 목록`}
                    onClick={() => setDrill({ metric: "reason", reason: r.code })}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDrill({ metric: "reason", reason: r.code }); } }}
                    style={{ width: `${width}%`, background: REASON_COLOR[r.code] || "var(--ink-4)", transition: "width .3s ease", cursor: "pointer" }}
                  />
                );
              })}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginTop: 10 }}>
              {reasons.filter(r => r.count > 0).map((r) => (
                <div key={r.code} role="button" tabIndex={0}
                  title={`${r.label} 사유로 이달 종결된 케이스 ${r.count}건 보기`}
                  onClick={() => setDrill({ metric: "reason", reason: r.code })}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDrill({ metric: "reason", reason: r.code }); } }}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: "pointer" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: REASON_COLOR[r.code] || "var(--ink-4)", flexShrink: 0 }}/>
                  <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{r.label}</span>
                  <span className="mono" style={{ color: "var(--ink-3)", fontWeight: 700 }}>{r.count}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {drill && (
        <MetricDrilldownModal
          endpoint="/api/v1/dashboard/region/closure_board/items"
          params={{ metric: drill.metric, reason: drill.reason }}
          regionCode={data.region?.code}
          columns={["어르신", "내용", "상태", "일자"]}
          emptyText="해당하는 케이스가 없습니다."
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

// 헤드라인 숫자 박스. onOpen 이 있으면 클릭 가능(그 숫자를 이루는 목록을 연다).
function StatBox({ label, children, onOpen, title, style }: any) {
  const clickable = typeof onOpen === "function";
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      title={title}
      onClick={clickable ? onOpen : undefined}
      onKeyDown={(e) => { if (clickable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen(); } }}
      style={{ padding: "12px 14px", background: "var(--bg-sub)", borderRadius: 10,
               cursor: clickable ? "pointer" : "default", ...(style || {}) }}
    >
      <div className="muted" style={{ fontSize: 10.5, fontWeight: 600 }}>{label}</div>
      {children}
    </div>
  );
}
